<template>
    <GmapMap
        v-if="markers && markers.length"
        ref="mapRef"
        :center="mapCenter"
        :zoom="11"
        :style="`width: 100%; height: ${height};`"
        @click="unsetInfoWindow"
    >
        <GmapInfoWindow :options="infoWinOptions" :position="infoWindowPos" :opened="infoWinOpen" @closeclick="unsetInfoWindow">
            <PropertyCard v-if="currentMarkerType === 'propertyUnit'"
                          :property="currentPropertyUnit"
                          :distance-to-school="currentDistanceToSchool"
            />
            <p v-if="currentMarkerType === 'school'">
                <v-card flat>
                    <v-card-title class="py-0">{{this.currentSchool.name}}</v-card-title>
                    <v-card-text class="py-0">{{this.currentSchool.address}}</v-card-text>
                </v-card>
            </p>
        </GmapInfoWindow>

        <template v-for="(m, index) in markers">
            <GmapMarker
                v-if="m.icon"
                :key="index"
                :ref="'marker-' + index"
                :position="m.position"
                :clickable="true"
                :draggable="false"
                :icon="getMarkerIcon(m)"
                :zIndex="isMarkerHighlighted(m) ? 1000 : 1"
                @click="toggleInfoWindow(m, index)"
            />
        </template>
    </GmapMap>
</template>

<script>
import { Map as GmapMap } from 'gmap-vue'
import { InfoWindow as GmapInfoWindow } from 'gmap-vue'
import { Marker as GmapMarker } from 'gmap-vue'
import PropertyCard from '../components/PropertyCard'

export default {
    name: "GoogleMap",

    components: { GmapMap, GmapInfoWindow, GmapMarker, PropertyCard },

    props: {
        markers: {
            type: Array,
            default: () => null
        },
        height: {
            type: String,
            default: () => '520px'
        },
        highlightedPropertyId: {
            type: [Number, String],
            default: null
        }
    },

    computed: {
        mapCenter() {
            // Center on property markers, not the school marker
            const propertyMarkers = this.markers.filter(m => m.type === 'propertyUnit');
            if (propertyMarkers.length > 0) {
                return propertyMarkers[0].position;
            }
            // Fallback to first marker if no properties
            return this.markers[0].position;
        }
    },

    watch: {
        markers: {
            handler() {
                this.unsetInfoWindow();
                this.fitMapBounds();
                this.$nextTick(() => {
                    this.attachMarkerListeners();
                });
            },
            deep: true
        }
    },

    mounted() {
        this.fitMapBounds();
        this.$nextTick(() => {
            this.attachMarkerListeners();
        });
    },

    data() {
        return {
            infoWindowPos: null,
            infoWinOpen: false,
            infoWinOptions: {
                pixelOffset: {
                    width: 0,
                    height: -35
                }
            },
            currentMarkerIndex: null,
            currentMarkerType: '',
            currentSchool: {},
            currentPropertyUnit: {},
            currentDistanceToSchool: null,
        }
    },

    methods: {
        fitMapBounds() {
            this.$nextTick(() => {
                if (!this.$refs.mapRef || !this.markers || this.markers.length === 0) return;

                this.$refs.mapRef.$mapPromise.then((map) => {
                    const bounds = new window.google.maps.LatLngBounds();

                    // Only include property markers in bounds calculation
                    const propertyMarkers = this.markers.filter(m => m.type === 'propertyUnit');

                    // Only fit bounds when we have property markers
                    // This prevents centering on school marker while properties are loading
                    if (propertyMarkers.length === 0) {
                        return;
                    }

                    propertyMarkers.forEach(m => {
                        bounds.extend(m.position);
                    });

                    // Cap zoom so clustered/same-address markers don't over-zoom
                    map.setOptions({ maxZoom: 15 });
                    map.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });
                    window.google.maps.event.addListenerOnce(map, 'idle', () => {
                        map.setOptions({ maxZoom: null });
                    });
                });
            });
        },
        unsetInfoWindow() {
            this.infoWindowPos = null
            this.infoWinOpen = false
            this.currentMarkerIndex = null
            this.currentMarkerType = ''
            this.currentSchool = {}
            this.currentPropertyUnit = {}
            this.currentDistanceToSchool = null
        },
        toggleInfoWindow(marker, index) {
            this.infoWindowPos = marker.position
            this.currentMarkerType = marker.type

            if ('school' === marker.type) {
                this.currentSchool = marker.school
            }

            if ('propertyUnit' === marker.type) {
                this.currentPropertyUnit = marker.propertyUnit
                this.currentDistanceToSchool = marker.distanceToSchool
            }

            this.infoWinOpen = true
            this.currentMarkerIndex = index
        },
        getMarkerIcon(marker) {
            const isHighlighted = marker.type === 'propertyUnit' &&
                marker.propertyUnit &&
                marker.propertyUnit.id === this.highlightedPropertyId;

            // Use highlighted orange icon for highlighted markers
            let iconFile = marker.icon;
            if (isHighlighted && marker.icon === 'map-building.svg') {
                iconFile = 'map-building-highlighted.svg';
            }

            return {
                url: require(`../assets/icons/${iconFile}`),
                scaledSize: isHighlighted
                    ? { width: 44, height: 44 }
                    : { width: 32, height: 32 }
            };
        },
        isMarkerHighlighted(marker) {
            return marker.type === 'propertyUnit' &&
                marker.propertyUnit &&
                marker.propertyUnit.id === this.highlightedPropertyId;
        },
        onMarkerHover(marker) {
            if (marker && marker.type === 'propertyUnit' && marker.propertyUnit) {
                this.$emit('marker-hover', marker.propertyUnit.id);
            } else {
                this.$emit('marker-hover', null);
            }
        },
        attachMarkerListeners() {
            if (!this.markers) return;

            this.markers.forEach((m, index) => {
                if (!m.icon) return;

                const markerRef = this.$refs['marker-' + index];
                if (!markerRef || !markerRef[0]) return;

                const markerComponent = markerRef[0];

                // Wait for the marker to be ready, then attach listeners
                const attachListeners = (googleMarker) => {
                    // Remove existing listeners to avoid duplicates
                    window.google.maps.event.clearListeners(googleMarker, 'mouseover');
                    window.google.maps.event.clearListeners(googleMarker, 'mouseout');

                    // Add mouseover listener
                    googleMarker.addListener('mouseover', () => {
                        this.onMarkerHover(m);
                    });

                    // Add mouseout listener
                    googleMarker.addListener('mouseout', () => {
                        this.onMarkerHover(null);
                    });
                };

                // Check if marker promise exists (gmap-vue way)
                if (markerComponent.$markerPromise) {
                    markerComponent.$markerPromise.then(attachListeners);
                } else if (markerComponent.$markerObject) {
                    // Fallback if marker is already ready
                    attachListeners(markerComponent.$markerObject);
                }
            });
        }
    }
}
</script>

<style>
    .vue-map-container {
        height: 300px;
    }
</style>
